import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Mail, Shield, Crown, UserPlus, Trash2, Loader2, AlertCircle, ChevronDown, Check, X, Info, CreditCard, Zap, Plus, ShoppingCart } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { teamApi, stripeApi, billingApi, roleSettingsApi } from '../services/api';
import { useSafeMode } from '../contexts/SafeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import AssignSeatsModal from '../components/billing/AssignSeatsModal';
import UpgradeContextModal from '../components/billing/UpgradeContextModal';
import { useUpgradeReturn } from '../hooks/useUpgradeReturn';

const ROLE_CONFIG = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/30',
    description: 'Full access including company settings',
    permissions: [
      { action: 'Manage company settings', allowed: true },
      { action: 'Invite anyone (Owner, PM, Collaborator)', allowed: true },
      { action: 'CRUD products/features', allowed: true },
      { action: 'Reorder priority', allowed: true },
      { action: 'Move Kanban status', allowed: true },
      { action: 'Use AI Co-Pilot', allowed: true },
    ]
  },
  product_manager: {
    label: 'Product Manager',
    icon: Shield,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    description: 'Can manage products, epics, and features',
    permissions: [
      { action: 'Manage company settings', allowed: false },
      { action: 'Invite collaborators only', allowed: true },
      { action: 'CRUD products/features', allowed: true },
      { action: 'Reorder priority', allowed: true },
      { action: 'Move Kanban status', allowed: true },
      { action: 'Use AI Co-Pilot', allowed: true },
    ]
  },
  collaborator: {
    label: 'Collaborator',
    icon: Users,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    description: 'Can update statuses and view products',
    permissions: [
      { action: 'Manage company settings', allowed: false },
      { action: 'Invite team members', allowed: false },
      { action: 'CRUD products/features', allowed: false },
      { action: 'Reorder priority', allowed: false },
      { action: 'Move Kanban status', allowed: true },
      { action: 'Use AI Co-Pilot', allowed: false },
    ]
  },
  billing_admin: {
    label: 'Billing Admin',
    icon: CreditCard,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    description: 'Can manage billing and subscriptions only',
    permissions: [
      { action: 'Manage company settings', allowed: false },
      { action: 'Manage billing & subscriptions', allowed: true },
      { action: 'Invite team members', allowed: false },
      { action: 'CRUD products/features', allowed: false },
      { action: 'View products', allowed: false },
      { action: 'Use AI Co-Pilot', allowed: false },
    ],
    paidOnly: true
  }
};

// Permission matrix rows for the comparison table (fallback if API unavailable)
const PERMISSION_MATRIX_FALLBACK = [
  { action: 'Manage company', owner: true, product_manager: false, collaborator: false, billing_admin: false },
  { action: 'Manage billing', owner: true, product_manager: false, collaborator: false, billing_admin: true },
  { action: 'Invite anyone', owner: true, product_manager: false, collaborator: false, billing_admin: false },
  { action: 'Invite collaborators', owner: true, product_manager: true, collaborator: false, billing_admin: false },
  { action: 'CRUD products/features', owner: true, product_manager: true, collaborator: false, billing_admin: false },
  { action: 'Reorder priority', owner: true, product_manager: true, collaborator: false, billing_admin: false },
  { action: 'Move Kanban status', owner: true, product_manager: true, collaborator: true, billing_admin: false },
  { action: 'Use AI Co-Pilot', owner: true, product_manager: true, collaborator: false, billing_admin: false },
];

// Object display names for dynamic matrix
const OBJECT_LABELS = {
  products: 'Products',
  epics: 'Epics',
  features: 'Features',
  notes: 'Notes',
  releases: 'Releases',
  initiatives: 'Initiatives',
  goals: 'Goals',
  personas: 'Personas',
  tags: 'Tags',
  requests: 'Requests',
  architecture: 'Architecture',
  delivery: 'Delivery',
  team_members: 'Team Members',
};

// Capability display names
const CAPABILITY_DISPLAY = {
  use_ai_copilot: 'AI Co-Pilot',
  reorder_priority: 'Reorder Priority',
  manage_billing: 'Manage Billing',
  manage_company: 'Manage Company',
};

const TeamPage = ({ embedded = false }) => {
  const { getThemeColors } = useSafeMode();
  const { refreshAuth } = useAuth();
  const colors = getThemeColors();
  const [searchParams] = useSearchParams();
  
  const [members, setMembers] = useState([]);
  const [myMembership, setMyMembership] = useState(null);
  const [counts, setCounts] = useState({});
  const [limits, setLimits] = useState(null);
  const [planTier, setPlanTier] = useState('free');
  const [loading, setLoading] = useState(true);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  
  // Seat Pool state
  const [seatStatus, setSeatStatus] = useState(null);
  
  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('collaborator');
  const [inviting, setInviting] = useState(false);
  
  // Role change state
  const [changingRole, setChangingRole] = useState(null);
  const [removingMember, setRemovingMember] = useState(null);
  
  // Confirmation modal
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Permissions matrix visibility
  const [showMatrix, setShowMatrix] = useState(false);
  
  // Dynamic permission matrix from RBAC API
  const [rbacRoles, setRbacRoles] = useState([]);
  const [rbacObjects, setRbacObjects] = useState([]);
  const [rbacLoading, setRbacLoading] = useState(false);
  
  // Purchase Seat Modal state
  const [showPurchaseSeatModal, setShowPurchaseSeatModal] = useState(false);
  const [purchaseSeatContext, setPurchaseSeatContext] = useState(null); // { action: 'invite' | 'upgrade', targetRole, memberId, email }
  const [purchasingSeats, setPurchasingSeats] = useState(false);
  const [seatsToPurchase, setSeatsToPurchase] = useState(1);
  
  // Upgrade modal for non-owners (PMs who hit seat limits)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null); // { action: 'invite' | 'upgrade', ...details }
  
  // Handle upgrade return (post-checkout flow)
  const {
    isProcessing: isProcessingCheckout,
    showAssignSeats,
    setShowAssignSeats,
    newSeatsCount,
  } = useUpgradeReturn({
    onUpgradeSuccess: () => {
      // Refresh data after successful checkout
      loadTeam();
      refreshAuth();
    },
  });
  
  const loadTeam = async () => {
    try {
      const [teamRes, meRes, billingContextRes] = await Promise.all([
        teamApi.list(),
        teamApi.getMyMembership(),
        billingApi.getContext().catch(() => null),  // Unified context endpoint
      ]);
      
      setMembers(teamRes.data.members || []);
      setCounts(teamRes.data.counts || {});
      setLimits(teamRes.data.limits);
      setPlanTier(teamRes.data.plan_tier || 'free');
      setMyMembership(meRes.data.member);
      
      // Use unified billing context for subscription and seat data
      if (billingContextRes?.data) {
        const ctx = billingContextRes.data;
        setSubscription({
          status: ctx.billing?.status,
          is_paid: ctx.billing?.is_paid,
          plan_label: ctx.plan?.label,
          plan_type: ctx.plan?.plan_type,
          price_per_seat: ctx.plan?.price_cents ? ctx.plan.price_cents / 100 : 0,
        });
        // Map unified seat data to expected format
        setSeatStatus({
          seats_purchased: ctx.seats?.purchased || 0,
          seats_comped: ctx.seats?.comped || 0,
          seats_total: ctx.seats?.total || 0,
          seats_occupied: ctx.seats?.occupied || 0,
          seats_available: ctx.seats?.available || 0,
          plan_max_seats: ctx.seats?.plan_max,
          can_add_seat: ctx.seats?.can_add_seat,
        });
      }
    } catch (err) {
      console.error('Failed to load team:', err);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };
  
  // Load RBAC permission matrix when matrix is opened
  const loadRbacMatrix = async () => {
    if (rbacRoles.length > 0) return; // Already loaded
    
    try {
      setRbacLoading(true);
      const response = await roleSettingsApi.list();
      const roles = response.data.member_roles || [];
      const objects = response.data.objects || [];
      
      setRbacRoles(roles);
      setRbacObjects(objects);
    } catch (err) {
      console.error('Failed to load RBAC matrix:', err);
      // Will fall back to static matrix
    } finally {
      setRbacLoading(false);
    }
  };
  
  useEffect(() => {
    // Check if returning from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');
    
    if (checkoutStatus === 'success') {
      // Sync subscription from Stripe (fallback in case webhook didn't fire)
      const syncAndLoad = async () => {
        try {
          toast.loading('Activating your subscription...');
          const syncResult = await stripeApi.syncSubscription();
          
          if (syncResult.data.status === 'synced') {
            toast.dismiss();
            toast.success(`🎉 Subscription activated! ${syncResult.data.seats} seat(s) added.`);
          } else if (syncResult.data.status === 'no_subscription') {
            toast.dismiss();
            toast.info('Processing your payment... please refresh in a moment.');
          }
        } catch (err) {
          toast.dismiss();
          console.error('Sync error:', err);
        }
        
        // Clean up URL and load team
        window.history.replaceState({}, '', '/team');
        loadTeam();
      };
      
      syncAndLoad();
    } else if (checkoutStatus === 'cancelled') {
      toast.info('Checkout cancelled');
      window.history.replaceState({}, '', '/team');
      loadTeam();
    } else {
      loadTeam();
    }
  }, []);
  
  const myPermissions = myMembership ? {
    canManageTeam: ['owner', 'product_manager', 'billing_admin'].includes(myMembership.role),
    canInviteRoles: myMembership.role === 'owner' || myMembership.role === 'billing_admin'
      ? planTier === 'paid' 
        ? ['owner', 'product_manager', 'collaborator', 'billing_admin']
        : ['owner', 'product_manager', 'collaborator']  // billing_admin requires paid
      : myMembership.role === 'product_manager'
        ? ['collaborator']
        : [],
    isOwner: myMembership.role === 'owner',
    isBillingAdmin: myMembership.role === 'billing_admin'
  } : {};
  
  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Check if inviting a paid role (owner or product_manager) and we need to check seat limits
    const isPaidRole = ['owner', 'product_manager'].includes(inviteRole);
    
    // Pre-check seat availability for paid roles
    if (isPaidRole && planTier === 'paid' && seatStatus) {
      if (seatStatus.available_seats <= 0) {
        // No seats available - show appropriate modal based on user role
        if (myPermissions.isOwner) {
          // Owner can purchase seats directly
          setShowInviteModal(false);
          setPurchaseSeatContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setSeatsToPurchase(1);
          setShowPurchaseSeatModal(true);
        } else {
          // Non-owner needs to request upgrade
          setShowInviteModal(false);
          setUpgradeContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setShowUpgradeModal(true);
        }
        return;
      }
    }
    
    // For free tier, check against free limits
    if (isPaidRole && planTier === 'free') {
      const currentPaidMembers = members.filter(m => 
        ['owner', 'product_manager'].includes(m.role)
      ).length;
      const freeLimit = limits?.members || 3;
      
      if (currentPaidMembers >= freeLimit) {
        // Hit free tier limit - show upgrade modal
        setShowInviteModal(false);
        if (myPermissions.isOwner) {
          setPurchaseSeatContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setShowPurchaseSeatModal(true);
        } else {
          setUpgradeContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setShowUpgradeModal(true);
        }
        return;
      }
    }
    
    setInviting(true);
    try {
      await teamApi.invite({
        email: inviteEmail.trim(),
        role: inviteRole,
        name: inviteName.trim() || null,
        origin_url: window.location.origin  // Pass current frontend origin for invite links
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('collaborator');
      loadTeam();
    } catch (err) {
      // Handle seat limit exceeded (402 error)
      if (err.response?.status === 402 && err.response?.data?.detail?.error === 'seat_limit_exceeded') {
        setShowInviteModal(false);
        if (myPermissions.isOwner) {
          setPurchaseSeatContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setSeatsToPurchase(1);
          setShowPurchaseSeatModal(true);
        } else {
          setUpgradeContext({
            action: 'invite',
            targetRole: inviteRole,
            email: inviteEmail.trim(),
            name: inviteName.trim() || null
          });
          setShowUpgradeModal(true);
        }
      } else {
        toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to send invitation');
      }
    } finally {
      setInviting(false);
    }
  };
  
  const handleRoleChange = async (memberId, newRole) => {
    // Find the member to get their current role
    const member = members.find(m => m.member_id === memberId);
    const isPaidRole = ['owner', 'product_manager'].includes(newRole);
    const wasFreeeRole = ['collaborator', 'billing_admin'].includes(member?.role);
    
    // Pre-check seat availability when upgrading to paid role
    if (isPaidRole && wasFreeeRole && planTier === 'paid' && seatStatus) {
      if (seatStatus.available_seats <= 0) {
        // No seats available
        if (myPermissions.isOwner) {
          setPurchaseSeatContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setSeatsToPurchase(1);
          setShowPurchaseSeatModal(true);
        } else {
          setUpgradeContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setShowUpgradeModal(true);
        }
        return;
      }
    }
    
    // For free tier upgrading to paid role
    if (isPaidRole && wasFreeeRole && planTier === 'free') {
      const currentPaidMembers = members.filter(m => 
        ['owner', 'product_manager'].includes(m.role)
      ).length;
      const freeLimit = limits?.members || 3;
      
      if (currentPaidMembers >= freeLimit) {
        if (myPermissions.isOwner) {
          setPurchaseSeatContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setShowPurchaseSeatModal(true);
        } else {
          setUpgradeContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setShowUpgradeModal(true);
        }
        return;
      }
    }
    
    setChangingRole(memberId);
    try {
      await teamApi.updateRole(memberId, newRole);
      toast.success('Role updated');
      loadTeam();
    } catch (err) {
      // Handle seat limit exceeded (402 error)
      if (err.response?.status === 402 && err.response?.data?.detail?.error === 'seat_limit_exceeded') {
        if (myPermissions.isOwner) {
          setPurchaseSeatContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setSeatsToPurchase(1);
          setShowPurchaseSeatModal(true);
        } else {
          setUpgradeContext({
            action: 'upgrade',
            targetRole: newRole,
            memberId: memberId,
            memberName: member?.name || member?.email
          });
          setShowUpgradeModal(true);
        }
      } else {
        toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to update role');
      }
    } finally {
      setChangingRole(null);
    }
  };
  
  const handleRemove = async (memberId) => {
    setRemovingMember(memberId);
    try {
      await teamApi.remove(memberId);
      toast.success('Member removed');
      setConfirmAction(null);
      loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setRemovingMember(null);
    }
  };
  
  // Handle purchasing additional seats
  const handlePurchaseSeats = async () => {
    if (seatsToPurchase < 1) {
      toast.error('Please select at least 1 seat');
      return;
    }
    
    setPurchasingSeats(true);
    try {
      const addResult = await stripeApi.addSeats(seatsToPurchase);
      toast.success(`${seatsToPurchase} seat(s) added to your subscription!`);
      
      // Close the modal
      setShowPurchaseSeatModal(false);
      
      // CRITICAL: Refresh seat status BEFORE retrying actions
      // This ensures the backend sees the updated seat count
      try {
        const [seatRes, teamRes] = await Promise.all([
          stripeApi.getSeatStatus(),
          teamApi.list()
        ]);
        
        if (seatRes?.data) {
          setSeatStatus(seatRes.data);
        }
        if (teamRes?.data) {
          setMembers(teamRes.data.members || []);
        }
      } catch (refreshErr) {
        console.warn('Failed to refresh data after seat purchase:', refreshErr);
      }
      
      // Small delay to ensure backend has processed the seat update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Retry the original action if context exists
      if (purchaseSeatContext) {
        try {
          if (purchaseSeatContext.action === 'invite') {
            // Retry the invite
            await teamApi.invite({
              email: purchaseSeatContext.email,
              role: purchaseSeatContext.targetRole,
              name: purchaseSeatContext.name,
              origin_url: window.location.origin  // Pass current frontend origin
            });
            toast.success(`Invitation sent to ${purchaseSeatContext.email}`);
            setInviteEmail('');
            setInviteName('');
            setInviteRole('collaborator');
          } else if (purchaseSeatContext.action === 'upgrade') {
            // Retry the role upgrade
            await teamApi.updateRole(purchaseSeatContext.memberId, purchaseSeatContext.targetRole);
            toast.success(`Role updated to ${ROLE_CONFIG[purchaseSeatContext.targetRole]?.label}`);
          }
        } catch (retryErr) {
          console.error('Error retrying action after seat purchase:', retryErr);
          toast.error(retryErr.response?.data?.detail || 'Seat added, but action failed. Please try again.');
        }
      }
      
      setPurchaseSeatContext(null);
      loadTeam();
    } catch (err) {
      console.error('Error purchasing seats:', err);
      toast.error(err.response?.data?.detail || 'Failed to purchase seats');
    } finally {
      setPurchasingSeats(false);
    }
  };
  
  const getRoleIcon = (role) => {
    const config = ROLE_CONFIG[role];
    const Icon = config?.icon || Users;
    return <Icon className={cn("w-4 h-4", config?.color)} />;
  };
  
  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
        </div>
      );
    }
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
        </div>
      </div>
    );
  }
  
  // Main content - render differently based on embedded mode
  const mainContent = (
    <>
      <div className={embedded ? "" : "max-w-5xl mx-auto"}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Team</h1>
            <p className={cn("text-sm mt-1", colors.textMuted)}>
              Manage your team members and their roles
            </p>
          </div>
          
          {myPermissions.canManageTeam && (
            <Button
              onClick={() => setShowInviteModal(true)}
              className={cn(colors.accentGradient, "text-white")}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>
          
          {/* Cancellation Warning Banner */}
          {subscription?.cancel_at_period_end && subscription?.subscription_ends_at && (
            <Card className="p-4 mb-4 border-2 border-orange-500 bg-orange-50 dark:bg-orange-900/20">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-orange-800 dark:text-orange-200">
                    Your subscription is scheduled to cancel
                  </p>
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    Your paid plan will end on <strong>{new Date(subscription.subscription_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                    After this date, all products will be archived and paid roles will be downgraded to Collaborator.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-500 text-orange-700 hover:bg-orange-100"
                  onClick={async () => {
                    const returnUrl = `${window.location.origin}/team`;
                    const result = await stripeApi.createPortalSession(returnUrl);
                    if (result.data.portal_url) {
                      window.location.href = result.data.portal_url;
                    }
                  }}
                >
                  Reactivate Plan
                </Button>
              </div>
            </Card>
          )}

          {/* Subscription & Seats Banner */}
          <Card className={cn("p-5 mb-6 border", colors.bgCard, colors.borderPrimary)}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <Badge 
                  variant="outline" 
                  className={cn(
                    "border px-3 py-1",
                    planTier === 'paid' 
                      ? subscription?.cancel_at_period_end 
                        ? 'border-orange-500 text-orange-600 bg-orange-50' 
                        : 'border-green-500 text-green-600 bg-green-50' 
                      : colors.borderPrimary
                  )}
                >
                  {planTier === 'paid' 
                    ? subscription?.cancel_at_period_end 
                      ? '⚠️ Cancelling' 
                      : '✨ Paid Plan' 
                    : 'Free Plan'}
                </Badge>
                {subscription?.access?.warning && (
                  <Badge variant="destructive" className="animate-pulse">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {subscription.access.warning.substring(0, 50)}...
                  </Badge>
                )}
              </div>
              {/* Billing buttons - only show when NOT embedded (standalone /team page) */}
              {!embedded && (
                <div className="flex items-center gap-2">
                  {/* Add Seats Button - Only for paid plans */}
                  {planTier === 'paid' && (myPermissions.isOwner || myPermissions.isBillingAdmin) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className={cn("border", colors.borderPrimary)}
                      onClick={() => {
                        setPurchaseSeatContext(null);
                        setSeatsToPurchase(1);
                        setShowPurchaseSeatModal(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Seats
                    </Button>
                  )}
                  {planTier === 'free' && myPermissions.isOwner && (
                    <Button 
                      size="sm" 
                      className={cn(colors.accentGradient, "text-white")}
                      onClick={() => {
                        // Navigate to billing page to start upgrade flow
                        window.location.href = '/settings?tab=billing';
                      }}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Plan
                    </Button>
                  )}
                  {planTier === 'paid' && (myPermissions.isOwner || myPermissions.isBillingAdmin) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      className={cn("border", colors.borderPrimary)}
                      onClick={async () => {
                        try {
                          const returnUrl = `${window.location.origin}/team`;
                          const result = await stripeApi.createPortalSession(returnUrl);
                          if (result.data.portal_url) {
                            window.location.href = result.data.portal_url;
                          }
                        } catch (err) {
                          toast.error('Failed to open billing portal');
                        }
                      }}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Manage Billing
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {/* Seat Pool Usage - For Paid Plans */}
            {planTier === 'paid' && seatStatus && (
              <div className="space-y-3 mb-4 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-blue-600" />
                    <span className={cn("text-sm font-semibold text-blue-900 dark:text-blue-100")}>Paid Seats</span>
                  </div>
                  <span className={cn("text-sm font-medium", 
                    seatStatus.seats_available === 0 ? 'text-amber-600' : 'text-blue-700 dark:text-blue-300'
                  )}>
                    {seatStatus.seats_occupied} of {seatStatus.seats_total} used
                    {seatStatus.seats_comped > 0 && (
                      <span className="text-xs ml-1 text-gray-500">
                        ({seatStatus.seats_purchased} purchased + {seatStatus.seats_comped} comped)
                      </span>
                    )}
                  </span>
                </div>
                <Progress 
                  value={(seatStatus.seats_occupied / Math.max(seatStatus.seats_total, 1)) * 100} 
                  className="h-2.5"
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-blue-700 dark:text-blue-400">
                    {seatStatus.seats_available} seat{seatStatus.seats_available !== 1 ? 's' : ''} available
                  </span>
                  {seatStatus.seats_available === 0 && (
                    <span className="text-amber-600 font-medium">
                      {seatStatus.plan_max_seats && seatStatus.seats_total >= seatStatus.plan_max_seats
                        ? 'Plan limit reached - upgrade to add more seats'
                        : seatStatus.plan_max_seats 
                          ? `On your plan you can purchase up to ${seatStatus.plan_max_seats - seatStatus.seats_total} more seat${seatStatus.plan_max_seats - seatStatus.seats_total !== 1 ? 's' : ''}`
                          : 'Purchase more seats to add paid roles'
                      }
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Seat Usage - Legacy for Free Plans */}
            {planTier === 'free' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className={cn("text-sm font-medium", colors.textPrimary)}>Seats Used</span>
                  <span className={cn("text-sm", colors.textMuted)}>
                    {members.length} / {(limits?.owner || 1) + (limits?.collaborator || 2) + (limits?.product_manager || 0)}
                  </span>
                </div>
                <Progress 
                  value={(members.length / ((limits?.owner || 1) + (limits?.collaborator || 2))) * 100} 
                  className="h-2"
                />
              </div>
            )}
            
            {/* Role Breakdown */}
            <div className="flex flex-wrap gap-4 text-xs pt-2">
              <span className={colors.textMuted}>
                <Crown className="w-3 h-3 inline mr-1 text-yellow-500" />
                Owners: <span className={cn(counts.owner >= (limits?.owner || 1) ? 'text-amber-500 font-medium' : colors.textPrimary)}>
                  {counts.owner || 0}{planTier === 'free' ? `/${limits?.owner || 1}` : ''}
                </span>
                {planTier === 'paid' && <span className="text-green-600 ml-1">(Paid seat)</span>}
              </span>
              <span className={colors.textMuted}>
                <Shield className="w-3 h-3 inline mr-1 text-blue-500" />
                PMs: <span className={cn(planTier === 'free' ? 'text-gray-400' : colors.textPrimary)}>
                  {counts.product_manager || 0}{planTier === 'free' ? '/0' : ''}
                </span>
                {planTier === 'free' && <span className="text-gray-400 ml-1">(Paid only)</span>}
                {planTier === 'paid' && <span className="text-green-600 ml-1">(Paid seat)</span>}
              </span>
              <span className={colors.textMuted}>
                <Users className="w-3 h-3 inline mr-1 text-green-500" />
                Collaborators: <span className={colors.textPrimary}>
                  {counts.collaborator || 0}{planTier === 'free' ? `/${limits?.collaborator || 2}` : ''}
                </span>
                <span className="text-green-600 ml-1">(Free)</span>
              </span>
              {planTier === 'paid' && (
                <span className={colors.textMuted}>
                  <CreditCard className="w-3 h-3 inline mr-1 text-purple-500" />
                  Billing Admins: <span className={colors.textPrimary}>
                    {counts.billing_admin || 0}
                  </span>
                  <span className="text-green-600 ml-1">(Free)</span>
                </span>
              )}
            </div>
            
            {/* Free tier info */}
            {planTier === 'free' && (
              <div className={cn("mt-4 pt-4 border-t text-xs", colors.borderPrimary, colors.textMuted)}>
                <Info className="w-3 h-3 inline mr-1" />
                Free plan includes 3 seats and 2 products. 
                Upgrade to Pro: <strong>${subscription?.price_per_seat || 0}/seat/month</strong> for Owners & PMs. <strong>Collaborators & Billing Admins are always free!</strong>
              </div>
            )}
            {planTier === 'paid' && (
              <div className={cn("mt-4 pt-4 border-t text-xs", colors.borderPrimary, colors.textMuted)}>
                <Info className="w-3 h-3 inline mr-1" />
                Pro plan: <strong>${subscription?.price_per_seat || 0}/seat/month</strong> for Owners & PMs. Collaborators & Billing Admins are free. 
                Pre-purchase seats to simplify role management.
              </div>
            )}
          </Card>
          
          {/* Team Members List */}
          <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
            <div className={cn("p-4 border-b", colors.borderPrimary)}>
              <h2 className={cn("font-semibold", colors.textPrimary)}>
                Team Members ({members.length})
              </h2>
            </div>
            
            <div className="divide-y divide-border">
              {members.map((member) => {
                const roleConfig = ROLE_CONFIG[member.role];
                const isMe = member.member_id === myMembership?.member_id;
                const canModify = myPermissions.canManageTeam && 
                  (myPermissions.isOwner || member.role === 'collaborator');
                
                return (
                  <div
                    key={member.member_id}
                    className={cn("p-4 flex items-center justify-between", colors.bgCard)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium",
                        roleConfig?.bgColor || 'bg-gray-500/20'
                      )}>
                        {(member.name && member.name[0]) ? member.name[0].toUpperCase() : (member.email && member.email[0]) ? member.email[0].toUpperCase() : '?'}
                      </div>
                      
                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={cn("font-medium", colors.textPrimary)}>
                            {member.name || (member.email ? member.email.split('@')[0] : 'Unknown')}
                          </span>
                          {isMe && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                          {member.status === 'pending' && (
                            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30 text-xs">
                              Pending
                            </Badge>
                          )}
                        </div>
                        <span className={cn("text-sm", colors.textMuted)}>{member.email || ''}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Role Badge/Select */}
                      {canModify && !isMe ? (
                        <Select
                          value={member.role}
                          onValueChange={(value) => handleRoleChange(member.member_id, value)}
                          disabled={changingRole === member.member_id}
                        >
                          <SelectTrigger className={cn("w-44", colors.bgInput, colors.borderPrimary)}>
                            <SelectValue>
                              <div className="flex items-center gap-2">
                                {getRoleIcon(member.role)}
                                <span>{roleConfig?.label}</span>
                              </div>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {myPermissions.canInviteRoles.map((role) => {
                              // Check if this role has reached its free tier limit
                              // But allow if member is already this role (so they can stay)
                              const roleCount = counts[role] || 0;
                              const roleLimit = limits ? limits[role] : null;
                              const isCurrentRole = member.role === role;
                              const isLimitReached = limits && roleLimit !== null && roleCount >= roleLimit && !isCurrentRole;
                              return (
                                <SelectItem key={role} value={role} disabled={isLimitReached}>
                                  <div className="flex items-center gap-2">
                                    {getRoleIcon(role)}
                                    <span>{ROLE_CONFIG[role]?.label}</span>
                                    {isLimitReached && <span className="text-xs text-red-500">(Paid only)</span>}
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={cn("gap-1", roleConfig?.bgColor, roleConfig?.color, roleConfig?.borderColor)}>
                          {getRoleIcon(member.role)}
                          {roleConfig?.label}
                        </Badge>
                      )}
                      
                      {/* Remove Button */}
                      {canModify && !isMe && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmAction({ type: 'remove', member })}
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {members.length === 0 && (
                <div className={cn("p-8 text-center", colors.textMuted)}>
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No team members yet</p>
                  <p className="text-sm mt-1">Invite your first team member to get started</p>
                </div>
              )}
            </div>
          </Card>
          
          {/* Role Permissions Matrix */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className={cn("font-semibold", colors.textPrimary)}>Role Permissions</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!showMatrix) loadRbacMatrix();
                  setShowMatrix(!showMatrix);
                }}
                className={cn("border", colors.borderPrimary)}
              >
                <Info className="w-4 h-4 mr-2" />
                {showMatrix ? 'Hide' : 'View'} Full Matrix
              </Button>
            </div>
            
            {/* Full Permission Matrix Table */}
            {showMatrix && (
              <Card className={cn("mt-4 p-4 border overflow-x-auto", colors.bgCard, colors.borderPrimary)}>
                {rbacLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className={cn("ml-2", colors.textMuted)}>Loading permissions...</span>
                  </div>
                ) : rbacRoles.length > 0 ? (
                  <>
                    <h4 className={cn("font-medium mb-4", colors.textPrimary)}>
                      Full Permission Matrix
                      <span className={cn("ml-2 text-xs font-normal", colors.textMuted)}>
                        (R = Read, C = Create, U = Update, D = Delete)
                      </span>
                    </h4>
                    
                    {/* Dynamic RBAC Matrix */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={cn("border-b", colors.borderPrimary)}>
                          <th className={cn("text-left py-2 pr-4 font-medium sticky left-0", colors.bgCard, colors.textSecondary)}>
                            Object
                          </th>
                          {rbacRoles.map(role => (
                            <th key={role.role} colSpan={4} className="text-center py-2 px-2">
                              <div className="flex items-center justify-center gap-1">
                                {role.role === 'owner' && <Crown className="w-4 h-4 text-yellow-500" />}
                                {role.role === 'product_manager' && <Shield className="w-4 h-4 text-blue-500" />}
                                {role.role === 'collaborator' && <Users className="w-4 h-4 text-green-500" />}
                                {role.role === 'billing_admin' && <CreditCard className="w-4 h-4 text-purple-500" />}
                                <span className={cn("text-xs font-medium", colors.textPrimary)}>
                                  {role.label?.split(' ')[0] || role.role}
                                </span>
                                {role.is_paid_role && (
                                  <span className="text-[10px] text-green-600">$</span>
                                )}
                              </div>
                              <div className="flex justify-center gap-1 mt-1 text-[10px] text-gray-400">
                                <span className="w-6">R</span>
                                <span className="w-6">C</span>
                                <span className="w-6">U</span>
                                <span className="w-6">D</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rbacObjects.map(obj => (
                          <tr key={obj} className={cn("border-b last:border-0", colors.borderPrimary)}>
                            <td className={cn("py-2 pr-4 font-medium sticky left-0", colors.bgCard, colors.textSecondary)}>
                              {OBJECT_LABELS[obj] || obj}
                            </td>
                            {rbacRoles.map(role => (
                              <React.Fragment key={`${role.role}-${obj}`}>
                                {['read', 'create', 'update', 'delete'].map(action => {
                                  const hasPermission = role.permissions?.[obj]?.[action];
                                  return (
                                    <td key={action} className="text-center py-2 px-1">
                                      {hasPermission ? (
                                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                                      ) : (
                                        <X className="w-4 h-4 text-red-300 mx-auto" />
                                      )}
                                    </td>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                          </tr>
                        ))}
                        
                        {/* Capabilities Section */}
                        <tr className={cn("border-t-2", colors.borderPrimary)}>
                          <td colSpan={1 + rbacRoles.length * 4} className={cn("py-2 font-medium", colors.textPrimary)}>
                            Capabilities
                          </td>
                        </tr>
                        {Object.entries(CAPABILITY_DISPLAY).map(([capKey, capLabel]) => (
                          <tr key={capKey} className={cn("border-b last:border-0", colors.borderPrimary)}>
                            <td className={cn("py-2 pr-4 font-medium sticky left-0", colors.bgCard, colors.textSecondary)}>
                              {capLabel}
                            </td>
                            {rbacRoles.map(role => (
                              <td key={`${role.role}-${capKey}`} colSpan={4} className="text-center py-2">
                                {role.capabilities?.[capKey] ? (
                                  <Check className="w-4 h-4 text-green-500 mx-auto" />
                                ) : (
                                  <X className="w-4 h-4 text-red-300 mx-auto" />
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <>
                    {/* Fallback Static Matrix */}
                    <h4 className={cn("font-medium mb-4", colors.textPrimary)}>Full Permission Matrix</h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={cn("border-b", colors.borderPrimary)}>
                          <th className={cn("text-left py-2 pr-4 font-medium", colors.textSecondary)}>Action</th>
                          <th className="text-center py-2 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Crown className="w-4 h-4 text-yellow-500" />
                              <span className={colors.textPrimary}>Owner</span>
                            </div>
                          </th>
                          <th className="text-center py-2 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Shield className="w-4 h-4 text-blue-500" />
                              <span className={colors.textPrimary}>PM</span>
                            </div>
                          </th>
                          <th className="text-center py-2 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Users className="w-4 h-4 text-green-500" />
                              <span className={colors.textPrimary}>Collaborator</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {PERMISSION_MATRIX_FALLBACK.map((row, idx) => (
                          <tr key={idx} className={cn("border-b last:border-0", colors.borderPrimary)}>
                            <td className={cn("py-2.5 pr-4", colors.textSecondary)}>{row.action}</td>
                            <td className="text-center py-2.5 px-4">
                              {row.owner ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-red-400 mx-auto" />
                              )}
                            </td>
                            <td className="text-center py-2.5 px-4">
                              {row.product_manager ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-red-400 mx-auto" />
                              )}
                            </td>
                            <td className="text-center py-2.5 px-4">
                              {row.collaborator ? (
                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                              ) : (
                                <X className="w-5 h-5 text-red-400 mx-auto" />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </Card>
            )}
          </div>
        </div>
      
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowInviteModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
              <h2 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>
                Invite Team Member
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className={colors.textSecondary}>Email Address *</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Name (optional)</Label>
                  <Input
                    value={inviteName}
                    onChange={(e) => setInviteName(e.target.value)}
                    placeholder="John Doe"
                    className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                </div>
                
                <div>
                  <Label className={colors.textSecondary}>Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {myPermissions.canInviteRoles.map((role) => {
                        const config = ROLE_CONFIG[role];
                        // Check if this role has reached its free tier limit
                        const roleCount = counts[role] || 0;
                        const roleLimit = limits ? limits[role] : null;
                        const isLimitReached = limits && roleLimit !== null && roleCount >= roleLimit;
                        const disabled = isLimitReached;
                        return (
                          <SelectItem 
                            key={role} 
                            value={role}
                            disabled={disabled}
                          >
                            <div className="flex items-center gap-2">
                              {getRoleIcon(role)}
                              <span>{config?.label}</span>
                              {disabled && <span className="text-xs text-red-500">(Paid only)</span>}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                {limits && inviteRole && (
                  <p className={cn("text-xs", colors.textMuted)}>
                    {ROLE_CONFIG[inviteRole]?.description}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  className={cn("border", colors.borderPrimary)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleInvite}
                  disabled={inviting || !inviteEmail.trim()}
                  className={cn(colors.accentGradient, "text-white")}
                >
                  {inviting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Inviting...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Confirm Remove Modal */}
      {confirmAction?.type === 'remove' && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/70" onClick={() => setConfirmAction(null)} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-red-500/10">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
                <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                  Remove Team Member
                </h2>
              </div>
              
              <p className={cn("mb-4", colors.textSecondary)}>
                Are you sure you want to remove <strong>{confirmAction.member?.name || confirmAction.member?.email || 'this member'}</strong> from the team?
              </p>
              
              <p className={cn("text-sm mb-6", colors.textMuted)}>
                They will lose access to all products and features.
              </p>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmAction(null)}
                  className={cn("border", colors.borderPrimary)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleRemove(confirmAction.member.member_id)}
                  disabled={removingMember === confirmAction.member.member_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {removingMember === confirmAction.member.member_id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Member
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Purchase Seat Modal */}
      {showPurchaseSeatModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/70" onClick={() => {
            setShowPurchaseSeatModal(false);
            setPurchaseSeatContext(null);
          }} />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Card className={cn("w-full max-w-md p-6", colors.bgCard)}>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-blue-500/10">
                  <ShoppingCart className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                  Purchase Additional Seats
                </h2>
              </div>
              
              {/* Context message */}
              {purchaseSeatContext && (
                <div className={cn("mb-4 p-3 rounded-lg border", colors.bgCard, colors.borderPrimary)}>
                  {purchaseSeatContext.action === 'invite' ? (
                    <p className={cn("text-sm", colors.textSecondary)}>
                      To invite <strong>{purchaseSeatContext.email}</strong> as a <strong>{ROLE_CONFIG[purchaseSeatContext.targetRole]?.label}</strong>, 
                      you need to purchase additional seats.
                    </p>
                  ) : (
                    <p className={cn("text-sm", colors.textSecondary)}>
                      To upgrade <strong>{purchaseSeatContext.memberName}</strong> to <strong>{ROLE_CONFIG[purchaseSeatContext.targetRole]?.label}</strong>, 
                      you need to purchase additional seats.
                    </p>
                  )}
                </div>
              )}
              
              {/* Seat status summary */}
              {seatStatus && (
                <div className={cn("mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border", colors.borderPrimary)}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className={colors.textMuted}>Purchased (billed):</span>
                    <span className={cn("font-medium", colors.textPrimary)}>
                      {seatStatus.seats_purchased || 0} seat{(seatStatus.seats_purchased || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {(seatStatus.seats_comped || 0) > 0 && (
                    <div className="flex justify-between text-sm mb-2">
                      <span className={colors.textMuted}>Comped (free):</span>
                      <span className={cn("font-medium text-green-600")}>{seatStatus.seats_comped} seat{seatStatus.seats_comped !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm mb-2">
                    <span className={colors.textMuted}>Total available:</span>
                    <span className={cn("font-medium", colors.textPrimary)}>
                      {seatStatus.seats_total || 0} seat{(seatStatus.seats_total || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className={colors.textMuted}>Currently occupied:</span>
                    <span className={cn("font-medium", colors.textPrimary)}>
                      {seatStatus.seats_occupied || 0} seat{(seatStatus.seats_occupied || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                    <span className={colors.textMuted}>Empty seats:</span>
                    <span className={cn("font-medium", (seatStatus.seats_available || 0) === 0 ? 'text-amber-600' : 'text-green-600')}>
                      {seatStatus.seats_available || 0} seat{(seatStatus.seats_available || 0) !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {seatStatus.plan_seat_limit && (
                    <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className={colors.textMuted}>Plan limit:</span>
                      <span className={cn("font-medium", colors.textPrimary)}>{seatStatus.plan_seat_limit} seats max</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Plan limit warning */}
              {seatStatus?.plan_seat_limit && (
                (seatStatus.seats_total || 0) >= seatStatus.plan_seat_limit ? (
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Plan limit reached.</strong> Your plan allows a maximum of {seatStatus.plan_seat_limit} seats. 
                      Upgrade your plan to purchase more seats.
                    </p>
                  </div>
                ) : (seatStatus.seats_total || 0) + seatsToPurchase > seatStatus.plan_seat_limit ? (
                  <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Warning:</strong> You can only purchase {seatStatus.plan_seat_limit - (seatStatus.seats_total || 0)} more seat(s) on your current plan.
                    </p>
                  </div>
                ) : null
              )}
              
              {/* Seat quantity selector */}
              <div className="mb-4">
                <Label className={colors.textSecondary}>Seats to purchase</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSeatsToPurchase(Math.max(1, seatsToPurchase - 1))}
                    disabled={seatsToPurchase <= 1}
                    className={cn("border", colors.borderPrimary)}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    value={seatsToPurchase}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      // Cap at plan limit if applicable
                      const maxAllowed = seatStatus?.plan_seat_limit 
                        ? seatStatus.plan_seat_limit - (seatStatus.seats_total || 0)
                        : 100;
                      setSeatsToPurchase(Math.min(value, Math.max(1, maxAllowed)));
                    }}
                    min={1}
                    max={seatStatus?.plan_seat_limit ? seatStatus.plan_seat_limit - (seatStatus.seats_total || 0) : undefined}
                    className={cn("w-20 text-center", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const maxAllowed = seatStatus?.plan_seat_limit 
                        ? seatStatus.plan_seat_limit - (seatStatus.seats_total || 0)
                        : 100;
                      setSeatsToPurchase(Math.min(seatsToPurchase + 1, Math.max(1, maxAllowed)));
                    }}
                    disabled={seatStatus?.plan_seat_limit && (seatStatus.seats_total || 0) + seatsToPurchase >= seatStatus.plan_seat_limit}
                    className={cn("border", colors.borderPrimary)}
                  >
                    +
                  </Button>
                </div>
              </div>
              
              {/* Pricing info */}
              <div className={cn("mb-6 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30")}>
                <div className="flex justify-between items-center">
                  <span className="text-blue-900 dark:text-blue-100 font-medium">
                    {seatsToPurchase} seat{seatsToPurchase !== 1 ? 's' : ''} × ${subscription?.price_per_seat || 0}/month
                  </span>
                  <span className="text-xl font-bold text-blue-600">
                    ${seatsToPurchase * (subscription?.price_per_seat || 0)}/mo
                  </span>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                  Prorated charge added to your current billing cycle
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPurchaseSeatModal(false);
                    setPurchaseSeatContext(null);
                  }}
                  className={cn("border", colors.borderPrimary)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePurchaseSeats}
                  disabled={purchasingSeats || seatsToPurchase < 1}
                  className={cn(colors.accentGradient, "text-white")}
                >
                  {purchasingSeats ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Purchasing...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Purchase {seatsToPurchase} Seat{seatsToPurchase !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
      
      {/* Assign Seats Modal (shown after checkout) */}
      <AssignSeatsModal
        isOpen={showAssignSeats}
        onClose={() => setShowAssignSeats(false)}
        newSeats={newSeatsCount}
        onComplete={() => {
          setShowAssignSeats(false);
          loadTeam();
          refreshAuth();
        }}
      />
      
      {/* Upgrade Context Modal for non-owners hitting seat limits */}
      <UpgradeContextModal
        isOpen={showUpgradeModal}
        onClose={() => {
          setShowUpgradeModal(false);
          setUpgradeContext(null);
        }}
        limitType="seats"
        currentUsage={seatStatus?.used_seats || 0}
        currentLimit={seatStatus?.seats_purchased || 0}
        returnPath="/team"
        contextMessage={
          upgradeContext?.action === 'invite'
            ? `You need more seats to invite ${upgradeContext?.email} as a ${ROLE_CONFIG[upgradeContext?.targetRole]?.label || 'team member'}.`
            : upgradeContext?.action === 'upgrade'
            ? `You need more seats to upgrade ${upgradeContext?.memberName} to ${ROLE_CONFIG[upgradeContext?.targetRole]?.label || 'a paid role'}.`
            : undefined
        }
        onUpgradeComplete={() => {
          setShowUpgradeModal(false);
          setUpgradeContext(null);
          loadTeam();
        }}
      />
    </>
  );
  
  // Return with or without sidebar wrapper based on embedded mode
  if (embedded) {
    return mainContent;
  }
  
  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 overflow-auto p-6">
        {mainContent}
      </div>
    </div>
  );
};

export default TeamPage;
